import { computed, inject, type InjectionKey, onUnmounted, provide, ref, watch } from 'vue';

export type EditValidity<Block extends string = string> = Partial<Record<Block, boolean>>;

export interface EditContext<Form extends object = object> {
  form: Form | null;
  showErrors: boolean;
  patchForm(patch: Partial<Form>): void;
  setValidity(block: string, isValid: boolean | null): void;
}

const EDIT_CONTEXT_KEY: InjectionKey<EditContext> = Symbol('edit-context');

export function isEditValid(validity: EditValidity) {
  return Object.values(validity).every(Boolean);
}

export function updateEditValidity<Block extends string>(
  validity: EditValidity<Block>,
  block: Block,
  isValid: boolean | null
): EditValidity<Block> {
  if (isValid === null) {
    const { [block]: _removed, ...rest } = validity;
    return rest as EditValidity<Block>;
  }

  return { ...validity, [block]: isValid };
}

export function provideEditContext<Form extends object>(context: EditContext<Form>) {
  provide(EDIT_CONTEXT_KEY, context as EditContext);
}

export function useEditContext<Form extends object = Record<string, unknown>>() {
  const context = inject(EDIT_CONTEXT_KEY, null);

  if (!context) {
    throw new Error('The edit context is not provided.');
  }

  return context as EditContext<Form>;
}

export function useEditBlockState(block: string) {
  const context = useEditContext();

  const isValid = ref(false);

  watch(isValid, (value) => context.setValidity(block, value), { immediate: true });

  const showErrors = computed({
    get: () => context.showErrors,
    set: (value: boolean) => (context.showErrors = value),
  });

  onUnmounted(() => context.setValidity(block, null));

  return { isValid, showErrors };
}
